// ============================================
// AlpenScreen Content Studio - Template Gallery
// File: frontend/src/components/ContentStudio/TemplateGallery.js
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas } from 'fabric';
import {
  Search,
  Loader,
  Layout,
  Utensils,
  ShoppingBag,
  Heart,
  Dumbbell,
  Building,
  FileText,
  Crown,
  Check,
  Eye,
  Home,
  Sparkles,
  GraduationCap,
  Car,
  Briefcase,
  PartyPopper
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const BASE_URL = API_URL.replace('/api', '');

// Category icons mapping
const categoryIcons = {
  all: Layout,
  restaurant: Utensils,
  retail: ShoppingBag,
  healthcare: Heart,
  fitness: Dumbbell,
  hotel: Building,
  general: FileText,
  realestate: Home,
  beauty: Sparkles,
  education: GraduationCap,
  automotive: Car,
  services: Briefcase,
  events: PartyPopper
};

const TemplateGallery = ({ onSelectTemplate }) => {
  const { t, i18n } = useTranslation();
  
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/templates/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data.categories || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (selectedCategory !== 'all') {
          params.append('category', selectedCategory);
        }

        const headers = {
          'Accept-Language': i18n.language
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/templates?${params}&includeCanvas=true`, { headers });
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [selectedCategory, i18n.language]);

  // Filter templates by search
  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load full template for preview
const handlePreview = async (template) => {
  setLoadingPreview(true);
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/templates/${template.slug}`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Accept-Language': i18n.language
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      setPreviewTemplate(data.template);
    }
  } catch (error) {
    console.error('Error loading template preview:', error);
    setPreviewTemplate(template); // Fallback
  } finally {
    setLoadingPreview(false);
  }
};

  // Category translations
  const getCategoryName = (categoryId) => {
    const names = {
      all: t('contentStudio.categories.all', 'Alle Vorlagen'),
      restaurant: t('contentStudio.categories.restaurant', 'Restaurant & Gastro'),
      retail: t('contentStudio.categories.retail', 'Einzelhandel'),
      healthcare: t('contentStudio.categories.healthcare', 'Gesundheit'),
      fitness: t('contentStudio.categories.fitness', 'Fitness & Wellness'),
      hotel: t('contentStudio.categories.hotel', 'Hotel & Tourismus'),
      general: t('contentStudio.categories.general', 'Allgemein'),
      realestate: t('contentStudio.categories.realestate', 'Immobilien'),
    beauty: t('contentStudio.categories.beauty', 'Beauty & Wellness'),
    education: t('contentStudio.categories.education', 'Bildung & Kurse'),
    automotive: t('contentStudio.categories.automotive', 'Automotive'),
    services: t('contentStudio.categories.services', 'Dienstleistungen'),
    events: t('contentStudio.categories.events', 'Events & Veranstaltungen')
    };
    return names[categoryId] || categoryId;
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder={t('contentStudio.searchTemplates', 'Vorlagen durchsuchen...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
        />
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        {categories.map(category => {
          const Icon = categoryIcons[category.id] || Layout;
          const isActive = selectedCategory === category.id;
          
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                isActive
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Icon size={18} />
              <span>{getCategoryName(category.id)}</span>
              {category.templateCount > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  isActive 
                    ? 'bg-white/20 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {category.templateCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader className="animate-spin text-purple-600" size={32} />
        </div>
      )}

      {/* Empty state */}
      {!loading && filteredTemplates.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <Layout className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('contentStudio.noTemplatesFound', 'Keine Vorlagen gefunden')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {t('contentStudio.tryDifferentSearch', 'Versuchen Sie eine andere Suche oder Kategorie')}
          </p>
        </div>
      )}

      {/* Templates Grid */}
      {!loading && filteredTemplates.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={onSelectTemplate}
              onPreview={() => handlePreview(template)}
            />
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSelect={() => {
            onSelectTemplate(previewTemplate);
            setPreviewTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// Canvas Preview Component
const CanvasPreview = ({ canvasData }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !canvasData) return;

    // Cleanup previous
    if (fabricRef.current) {
      try {
        fabricRef.current.dispose();
      } catch (e) {
        // ignore
      }
      fabricRef.current = null;
    }

    // Create new canvas element
    if (canvasRef.current) {
      canvasRef.current.remove();
    }
    
    const canvasEl = document.createElement('canvas');
    canvasEl.width = 960;
    canvasEl.height = 540;
    containerRef.current.appendChild(canvasEl);
    canvasRef.current = canvasEl;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(async () => {
      try {
        const canvas = new Canvas(canvasEl, {
          width: 960,
          height: 540,
          selection: false,
          interactive: false,
          renderOnAddRemove: false
        });

        fabricRef.current = canvas;

        const data = typeof canvasData === 'string' ? JSON.parse(canvasData) : canvasData;
        
        await canvas.loadFromJSON(data);
        canvas.setZoom(0.5);
        canvas.renderAll();
      } catch (error) {
        console.error('Preview render error:', error);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (fabricRef.current) {
        try {
          fabricRef.current.dispose();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [canvasData]);

  return <div ref={containerRef} className="flex items-center justify-center" />;
};

// Small Thumbnail Preview
const ThumbnailPreview = ({ canvasData, templateSlug }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !canvasData) return;

    // Cleanup previous
    if (fabricRef.current) {
      try {
        fabricRef.current.dispose();
      } catch (e) {
        // ignore
      }
      fabricRef.current = null;
    }

    // Create new canvas element
    if (canvasRef.current) {
      canvasRef.current.remove();
    }
    
    const canvasEl = document.createElement('canvas');
    canvasEl.width = 384;
    canvasEl.height = 216;
    containerRef.current.appendChild(canvasEl);
    canvasRef.current = canvasEl;

    const timer = setTimeout(async () => {
      try {
        const canvas = new Canvas(canvasEl, {
          width: 384,
          height: 216,
          selection: false,
          interactive: false,
          renderOnAddRemove: false
        });

        fabricRef.current = canvas;

        const data = typeof canvasData === 'string' ? JSON.parse(canvasData) : canvasData;
        
        await canvas.loadFromJSON(data);
        canvas.setZoom(0.2); // Smaller zoom for thumbnails
        canvas.renderAll();
      } catch (error) {
        console.error('Thumbnail render error:', error);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (fabricRef.current) {
        try {
          fabricRef.current.dispose();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [canvasData, templateSlug]);

  return <div ref={containerRef} className="w-full h-full flex items-center justify-center" />;
};

// Template Card Component
const TemplateCard = ({ template, onSelect, onPreview }) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  // Generate preview from canvas data if no thumbnail
  const getPreviewStyle = () => {
    if (template.color_scheme?.primary) {
      return { backgroundColor: template.color_scheme.primary };
    }
    return { backgroundColor: '#1a1a2e' };
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-xl transition-all group">
      {/* Preview */}
      <div 
        className="aspect-video relative cursor-pointer overflow-hidden"
        style={getPreviewStyle()}
        onClick={onPreview}
      >
        {template.thumbnail_url ? (
  <img 
  src={`${BASE_URL}${template.thumbnail_url}`} 
  alt={template.name}
  className="w-full h-full object-cover"
/>
) : (
  <div className="w-full h-full flex items-center justify-center">
    <Layout className="text-white/30" size={48} />
  </div>
)}

        {/* Premium badge */}
        {template.is_premium && (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-lg">
            <Crown size={12} />
            Premium
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
          >
            <Eye size={18} />
            {t('contentStudio.preview', 'Vorschau')}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(template); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Check size={18} />
            {t('contentStudio.useTemplate', 'Verwenden')}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          {template.name}
        </h3>
        {template.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
            {template.description}
          </p>
        )}
        
        {/* Tags */}
        {template.tags && template.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {template.tags.slice(0, 3).map(tag => (
              <span 
                key={tag}
                className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Template Preview Modal
const TemplatePreviewModal = ({ template, onClose, onSelect }) => {
  const { t } = useTranslation();

  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const getPreviewStyle = () => {
    if (template.color_scheme?.primary) {
      return { backgroundColor: template.color_scheme.primary };
    }
    return { backgroundColor: '#1a1a2e' };
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Preview */}
<div 
  className="aspect-video relative flex items-center justify-center"
  style={getPreviewStyle()}
>
  {template.canvas_data ? (
    <CanvasPreview canvasData={template.canvas_data} />
  ) : template.thumbnail_url ? (
    <img 
      src={template.thumbnail_url} 
      alt={template.name}
      className="w-full h-full object-contain"
    />
  ) : (
    <div className="w-full h-full flex items-center justify-center">
      <Layout className="text-white/30" size={96} />
    </div>
  )}

          {/* Premium badge */}
          {template.is_premium && (
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-lg">
              <Crown size={16} />
              Premium
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {template.name}
              </h2>
              {template.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {template.description}
                </p>
              )}
            </div>
          </div>

          {/* Color scheme */}
          {template.color_scheme && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('contentStudio.colorScheme', 'Farbschema')}
              </h4>
              <div className="flex gap-2">
                {Object.entries(template.color_scheme).map(([name, color]) => (
                  <div 
                    key={name}
                    className="w-8 h-8 rounded-lg shadow-inner border border-gray-200 dark:border-gray-700"
                    style={{ backgroundColor: color }}
                    title={`${name}: ${color}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {template.tags.map(tag => (
                <span 
                  key={tag}
                  className="text-sm px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button
              onClick={onSelect}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              <Check size={20} />
              {t('contentStudio.useThisTemplate', 'Diese Vorlage verwenden')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateGallery;